
import BookingDeparture from "@/components/bookingflow/steps/departure/BookingDeparture.vue";
import useDepartures from "@/composables/useDepartures";
import { resourceCode } from "@/config/global.strings";
import { DirectionEnum, ModeOfTravelEnum, PassengerTypeEnum } from "@/models/enums";
import { DeparturePrice } from "@/models/www/DeparturePrice";
import { useBookingStore } from "@/store/booking";
import discountService from "@/services/discount.service";

import {
	computed,
	defineComponent,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import { debounce } from "vue-composable";
import useBookingCalendar from "@/composables/useBookingCalendar";
import useCalculatePriceEffect from "@/composables/useCalculatePriceEffect";
import { getQueryParamValue, removeQueryParams } from "@/helpers/urlHelper";
import { toBritishDate } from "@/helpers/dateHelper";
import { useRoute } from 'vue-router';

export default defineComponent({
	components: {
		BookingDeparture,
	},
	setup() {
		
		const route = useRoute();
		
		const bookingStore = useBookingStore();
		
		useBookingCalendar(true);
		useCalculatePriceEffect();
		
		const {
			getAvailableDepartures,
			outwardDepartures,
			returnDepartures,
			fetchDepartures,
		} = useDepartures();
		
		const isReturnOnlyAmendment = bookingStore
										.booking
										.amendmentMode === true &&
									bookingStore
										.booking
										.outward
										.isLocked === true &&
									bookingStore
										.booking
										.return
										.isLocked === false;
									  
		const loading = ref<boolean>(false);
		
		const outwardDeparture = computed(() =>
			bookingStore.getDeparture(DirectionEnum.Outward)
		);

		const returnDeparture = computed(() =>
			bookingStore.getDeparture(DirectionEnum.Return)
		);

		watch(outwardDeparture, () => {
			returnDepartures.value = getAvailableDepartures(
				returnDepartures.value as DeparturePrice[]
			);
		});
		
		const direction = computed(() => {
			
			const outwardDep = outwardDepartures.value.find(
				(d) => d.departureId === outwardDeparture.value.id
			);

			if (outwardDep?.transfer) {
				return DirectionEnum.Outward;
			}
			else {
				return DirectionEnum.Return;
			}
		});

		const transferAvailable = computed(() => {
			const outwardDep = outwardDepartures.value.find(
				(d) => d.departureId === outwardDeparture.value.id
			);
			const returnDep = returnDepartures.value.find(
				(d) => d.departureId === returnDeparture.value.id
			);
			
			if (bookingStore.booking.modeOfTravel == ModeOfTravelEnum.Skybus) {
					return false; //Skybus
			}
			else if (outwardDep?.transfer &&
				returnDep?.transfer) {
				return false; //Skybus
			}
			else if (outwardDep?.transfer) {
				return true; //FlySail
			}
			else if (returnDep?.transfer) {
				return true; //SailFly
			}
			return false; //Should never get here, but Scillonian and just in case
		});

		const includeReturn = computed(() => {
			return bookingStore.getBooking.tripType === DirectionEnum.Return;
		});

		const debouncedDepartures = debounce(() => {
			fetchDepartures().finally(() => (loading.value = false));
		}, 400);

		const outwardDate = computed(() => {
			return bookingStore.getDate(DirectionEnum.Outward);
		});
		
		const returnDate = computed(() => {
			return bookingStore.getDate(DirectionEnum.Return);
		});

		let unsubscribe = ref<any[]>([]);
		onMounted(async () => {
		
			loading.value = true
			
			await applyQueryParameters();
			
			fetchDepartures().finally(() => (loading.value = false));

			unsubscribe.value.push(
				watch(outwardDate, () => {
					loading.value = true;
					debouncedDepartures();
				})
			);

			unsubscribe.value.push(
				watch(returnDate, () => {
					loading.value = true;
					debouncedDepartures();
				})
			);
		});

		onBeforeUnmount(() => {
			if (unsubscribe.value) {
				unsubscribe.value.forEach((v) => v());
			}
		});
		
		const applyQueryParameters = async () => {
			
			const travelType : ModeOfTravelEnum = +getQueryParamValue('modeOfTravel');
			const passengerTypes = getQueryParamValue('passengerTypes');
			const voucherCode = getQueryParamValue('voucher');
			
			if (passengerTypes.length > 0) {
				bookingStore.setPassengers(DirectionEnum.Outward, 
										   passengerTypes
										   .split('')
										   .map((type) => ({ type: type as PassengerTypeEnum, id: 0 })))
			}
			
			if (getQueryParamValue('outwardDate').length > 0) {
				bookingStore.setDate(DirectionEnum.Outward, toBritishDate(getQueryParamValue('outwardDate'), 'yyyy-MM-dd'));
			}
			
			if (getQueryParamValue('returnDate').length > 0) {
				bookingStore.setDate(DirectionEnum.Return, toBritishDate(getQueryParamValue('returnDate'), 'yyyy-MM-dd'));
			}
			
			const outwardRoute = getQueryParamValue('leavingFrom');
			const returnRoute = getQueryParamValue('goingTo');
			
			if (returnRoute.length > 0) {
				bookingStore.setToDestination(returnRoute, travelType);
			}
			
			if (outwardRoute.length > 0) {
				bookingStore.setFromDestination(outwardRoute, travelType);
				bookingStore.setRoutes(travelType);
			}
			
			if (voucherCode.length > 0) {
			
				await discountService.apply(voucherCode, true);
			}
			
			removeQueryParams(['voucher', 'passengerTypes', 'modeOfTravel', 'leavingFrom', 'goingTo']);
		};

		return {
			includeReturn,
			DirectionEnum,
			transferAvailable,
			direction,
			loading,
			outwardDepartures,
			returnDepartures,
			resourceCode,
			isReturnOnlyAmendment,
			ModeOfTravelEnum
		};
	},
});
